export interface Formatversion {
  id: string;
  yyyymm: string;
  format_version: string;
  path: string;
  built_at: string;
  source_commit: string | null;
  counts: { msgtypes?: number; pruefids?: number; conditions?: number; packages?: number };
}

export interface ValidationFindingLocation {
  segment_tag: string;
  segment_index: number;
  data_element?: string | null;
  value?: string | null;
}

export interface ValidationFinding {
  code: string;
  message_de: string;
  location: ValidationFindingLocation;
  condition_key?: string | null;
  reason?: string | null;
}

export interface ValidationStatistics {
  segments_processed: number;
  elements_validated: number;
  errors: number;
  warnings: number;
}

export interface ValidationResult {
  valid: boolean;
  statistics: ValidationStatistics;
  errors: ValidationFinding[];
  warnings: ValidationFinding[];
  infos: ValidationFinding[];
  vorgaenge?: Array<{
    vorgang: {
      index: number;
      vorgang_id?: string;
      start_segment_index: number;
      trigger_segment_tag: string;
    };
    valid: boolean;
    errors: ValidationFinding[];
    warnings: ValidationFinding[];
    infos: ValidationFinding[];
    statistics: ValidationStatistics;
  }>;
  header_result?: unknown;
}

async function postForm<T>(url: string, fields: Record<string, string>): Promise<T> {
  const body = new FormData();
  for (const [k, v] of Object.entries(fields)) body.append(k, v);
  const r = await fetch(url, { method: "POST", body });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json() as Promise<T>;
}

export interface ConditionEntry {
  condition_key: string;
  condition_text: string;
  impl_kind?: string;
  impl_hint?: string;
  impl_code_ref?: string;
}

export interface CoverageFormatEntry {
  format: string;
  total: number;
  implemented: number;
  unimplemented_count: number;
  format_not_evaluable_count: number;
  implemented_list?: ConditionEntry[];
  unimplemented: ConditionEntry[];
  format_not_evaluable: ConditionEntry[];
}

export type ConditionStatus = "implemented" | "unimplemented" | "format_not_evaluable";

export interface ConditionRow {
  format: string;
  condition_key: string;
  condition_text: string;
  status: ConditionStatus;
  impl_kind?: string;
  impl_hint?: string;
  impl_code_ref?: string;
}

export interface Coverage {
  formatversion: string;
  totals: {
    conditions: number;
    implemented: number;
    unimplemented: number;
    format_not_evaluable: number;
  };
  per_format: CoverageFormatEntry[];
}

export const validatorApi = {
  async getCoverage(formatversionId: string): Promise<Coverage> {
    const r = await fetch(`/api/validator/coverage?formatversion=${encodeURIComponent(formatversionId)}`);
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    return r.json() as Promise<Coverage>;
  },
  async getFormatversions(): Promise<Formatversion[]> {
    const r = await fetch("/api/validator/formatversions");
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    const body = (await r.json()) as { formatversions: Formatversion[] };
    return body.formatversions;
  },
  getMessageTypes: (formatversionId: string) =>
    fetch(`/api/validator/message-types?formatversion=${encodeURIComponent(formatversionId)}`).then(
      (r) => r.json() as Promise<{ message_types: string[] }>,
    ),

  validate: (raw: string, formatversionId: string, pruefid?: string) => {
    const fields: Record<string, string> = { raw };
    if (pruefid) fields.pruefidentifikator = pruefid;
    return postForm<ValidationResult>(
      `/api/validator/validate?formatversion=${encodeURIComponent(formatversionId)}`,
      fields,
    );
  },
  convertAndValidate: (raw: string, formatversionId: string, pruefid?: string) => {
    const fields: Record<string, string> = { raw };
    if (pruefid) fields.pruefidentifikator = pruefid;
    return postForm<{ convert: unknown; validation: ValidationResult }>(
      `/api/validator/convert-and-validate?formatversion=${encodeURIComponent(formatversionId)}`,
      fields,
    );
  },
};
